import { motion } from "framer-motion";
import useSEO from "@/hooks/useSEO";
import RevealText from "@/components/RevealText";
import Marquee from "@/components/Marquee";

const PORTRAIT =
  "https://images.unsplash.com/photo-1529747598642-d0b4e3667314?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85";

const stats = [
  { n: "40+", l: "Communities served" },
  { n: "14", l: "Distinct disciplines" },
  { n: "24/7", l: "Operational posture" },
  { n: "1", l: "Operating standard" },
];

export default function About() {
  useSEO({
    title: "The Standard — About Manitoba Security Services",
    description:
      "Manitoba Security Services is a provincially-focused security firm serving every community in Manitoba with mining, mobile patrol, event, corporate, and industrial security disciplines.",
    keywords: [
      "about Manitoba Security Services",
      "Manitoba security company",
      "security firm Winnipeg",
      "Manitoba-only security",
    ],
  });

  return (
    <div className="bg-[#050505] pt-32 md:pt-40">
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 pb-16 md:pb-24 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <p className="font-editorial italic text-[#FF3B30] text-sm">
            Index — 04
          </p>
        </div>
        <div className="col-span-12 md:col-span-9">
          <RevealText
            lines={["The Standard."]}
            className="font-display font-black uppercase tracking-tightest leading-[0.88] text-white text-5xl md:text-7xl lg:text-8xl"
            stagger={0}
            delay={0.05}
          />
          <p className="mt-8 font-body text-lg md:text-xl text-[#a0a0a0] max-w-3xl leading-relaxed">
            Manitoba Security Services is a provincially-focused security firm.
            We do not chase national contracts. Our operating capacity is
            directed entirely to Manitoba communities — the geography, the
            industries, the winters, and the people we serve.
          </p>
        </div>
      </section>

      <Marquee items={["THE ELITE STANDARD", "MANITOBA ONLY", "VIGILANCE", "AUTHORITY"]} />

      {/* Statement + portrait */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-10 py-24 md:py-32 grid grid-cols-12 gap-6 md:gap-10">
        <div className="col-span-12 md:col-span-5">
          <div className="aspect-[4/5] overflow-hidden bg-[#0b0b0b] clip-editorial">
            <img
              src={PORTRAIT}
              alt="Manitoba Security personnel"
              className="h-full w-full object-cover grayscale contrast-125"
              loading="lazy"
            />
          </div>
        </div>
        <div className="col-span-12 md:col-span-7 space-y-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              01 — What we serve
            </p>
            <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-3xl md:text-4xl leading-tight">
              A province the size of a country.
            </h2>
            <p className="mt-6 font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75]">
              Manitoba runs from the 49th parallel to the Hudson Bay coastline
              at Churchill. Our clients operate in every part of that
              geography: mines in the Thompson nickel belt, refineries and
              concentrators in Flin Flon, corporate towers on Portage and Main,
              food processing in Portage la Prairie, energy operations in
              Virden and Melita, healthcare campuses in Winnipeg and Brandon,
              cottage country across the Whiteshell. One operating standard
              reaches all of it.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              02 — How we hire
            </p>
            <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-3xl md:text-4xl leading-tight">
              Selection over recruitment.
            </h2>
            <p className="mt-6 font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75]">
              Every officer is credentialed under Manitoba Justice private
              security regulations. Beyond that baseline, we select for
              communication, composure, and the discipline required to hold a
              posture through the seventh hour of a night shift. The officers
              you see at your site are the ones we chose specifically for your
              site.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-editorial italic text-[#FF3B30] text-sm">
              03 — How we operate
            </p>
            <h2 className="mt-4 font-display font-black uppercase tracking-tight text-white text-3xl md:text-4xl leading-tight">
              Documented. Auditable. Presented.
            </h2>
            <p className="mt-6 font-body text-base md:text-lg text-[#a0a0a0] leading-[1.75]">
              GPS-verified patrol checkpoints, time-stamped reports, incident
              logs with photograph evidence, coordinated liaison with the
              Winnipeg Police Service and RCMP detachments across the province,
              integrated communication with client dispatch and control rooms.
              Presence you can measure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-[#1e1e1e] bg-[#0b0b0b] py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.l}>
              <p className="font-display font-black text-white text-5xl md:text-7xl leading-none">
                {s.n}
              </p>
              <p className="mt-3 font-body uppercase tracking-[0.16em] text-[#a0a0a0] text-xs">
                {s.l}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
